import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import SearchTable2 from "../../../components/Tables/SearchTable2";
import { printNAsService } from "../../../services/printNAsService";
import Loading from "components/Loading/Loading";
import { getEmptyPageResponse, toTablePageableData } from "utils/utils";
import { FaFile, FaFilePdf, FaNewspaper, FaPaperPlane, FaPrint } from "react-icons/fa";
import { useCustomToast } from "components/CustomToast/CustomToast";

const PrintNAsList = () => {
  const { t } = useTranslation();
  const [response, setResponse] = useState(undefined);
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [isLoadingResponseFile, setIsLoadingResponseFile] = useState(false);
  const showToast = useCustomToast();
  const [searchParams, setSearchParams] = useState({});
  const [includeSentBatches, setIncludeSentBatches] = useState(false);

  const columns = useMemo(() => ([
    {
      Header: t("print-nas.list.table.header.name"),
      accessor: "nome",
    },
    {
      Header: t("print-nas.list.table.header.nas-printing"),
      accessor: "data_impressao_notificacoes_autuacao",
      custom: ({ value }) => {
        if (!value) { return t('print-nas.list.table.labels.no-print'); }
      }
    },
    {
      Header: t("print-nas.list.table.header.nas-sent"),
      accessor: "data_envio_notificacoes_autuacao",
      custom: ({ value }) => {
        if (!value) { return t('print-nas.list.table.labels.no-sent'); }
      }
    },
    {
      Header: t("print-nas.list.table.header.printing-after-date"),
      accessor: "data_limite_impressao_notificacoes_autuacao",
      custom: ({ value }) => {
        if (!value) { return "-"; }
      }
    },
    {
      Header: t('print-nas.list.table.header.actions'),
      disableSortBy: true,
      actions: [
        {
          label: t('print-nas.list.table.actions.print-na'),
          onClick: (row) => handlePrintNa(row.original.id),
          icon: <FaPrint />
        },
        {
          label: t('print-nas.list.table.actions.response-file'),
          onClick: (row) => handleResponseFile(row.original.id),
          icon: <FaFile />
        },
        {
          label: t('print-nas.list.table.actions.confirm-shipping'),
          onClick: (row) => handleConfirmShipping(row.original.id),
          icon: <FaPaperPlane />
        },
        {
          label: t('print-nas.list.table.actions.report'),
          onClick: (row) => handleBatchReport(row.original.id),
          icon: <FaFilePdf />
        },
        {
          label: t('print-nas.list.table.actions.notice'),
          onClick: (row) => handleEditalReport(row.original.id),
          icon: <FaNewspaper />,
          visible: (row) => row.edital_action
        }
      ]
    }
  ]), [includeSentBatches, searchParams]);

  useEffect(() => {
    fetchData();
  }, [includeSentBatches]);

  const handlePrintNa = async (id) => {
    try {
      setIsLoadingPrint(true);

      const { data, headers } = await printNAsService.printNa(id);
      const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: fileName
      }).click();

      URL.revokeObjectURL(url);
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingPrint(false);
      fetchData();
    }
  };

  const handleResponseFile = async (id) => {
    try {
      setIsLoadingResponseFile(true);

      const { data, headers } = await printNAsService.getResponseFileNa(id);
      const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: fileName
      }).click();

      URL.revokeObjectURL(url);
      fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingResponseFile(false);
    }
  };

  const handleConfirmShipping = async (id) => {
    try {
      await printNAsService.confirmShippingNa(id);
      showToast({ title: t("print-nas.messages.success.confirm-shipping-na"), status: "success" });
      fetchData();
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  };

  const handleBatchReport = async (id) => {
    setIsLoadingResponseFile(true);
    try {
      const { data, headers } = await printNAsService.getReportNa(id);
      const filename = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: filename
      }).click();

      URL.revokeObjectURL(url);
      showToast({ title: t("print-nas.messages.success.details-file-na"), status: "success" });
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingResponseFile(false);
      fetchData();
    }
  };

  const handleEditalReport = async (id) => {
    setIsLoadingResponseFile(true);
    try {
      const { data, headers } = await printNAsService.getEditalReportNA(id);
      const filename = headers['content-disposition'].match(/filename="(.+)"/)[1];

      const url = URL.createObjectURL(new Blob([data]));
      Object.assign(document.createElement('a'), {
        href: url,
        download: filename
      }).click();

      URL.revokeObjectURL(url);
      showToast({ title: t("print-nas.messages.success.edital-file-na"), status: "success" });
    } catch (error) {
      showToast({ title: t(error), status: "error" });
    } finally {
      setIsLoadingResponseFile(false);
      fetchData();
    }
  }

  const fetchData = async (params = {}) => {
    const finalParams = {
      ...searchParams,
      ...params,
      orderBy: params.orderBy ?? searchParams.orderBy ?? "nome",
      orderDirection: params.orderDirection ?? searchParams.orderDirection ?? "asc",
      filters: {
        ...searchParams.filters,
        ...params.filters,
        incluir_lotes_enviados: includeSentBatches ? "true" : "false",
      },
    };

    setSearchParams(finalParams);
    setResponse(undefined);

    try {
      return setResponse(await printNAsService.list(finalParams));
    } catch (err) {
      setResponse(getEmptyPageResponse(finalParams));
      showToast({ title: t(err), status: "error" });
    }
  };

  return (
    <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
      <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
        <ListPageCardHeader title={t("print-nas.title")} subtitle={t("print-nas.description")} />
        <CardBody position="relative">
          {response ? (
            <Box position="relative">
              <SearchTable2
                columnsData={columns}
                onChange={fetchData}
                tableData={response.content}
                pageableData={toTablePageableData(response, { defaultField: "data_limite_impressao_notificacoes_penalidade", ...searchParams })}
                includeSentBatches={{
                  value: includeSentBatches,
                  onChange: setIncludeSentBatches
                }}
              />
              {(isLoadingPrint || isLoadingResponseFile) && (
                <Box
                  position="fixed"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  bg="rgba(255, 255, 255, 0.6)"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  zIndex="overlay"
                >
                  <Loading maxH={{ sm: "100px", md: "350px" }} />
                </Box>
              )}
            </Box>
          ) : (
            <Loading maxH={{ sm: "100px", md: "350px" }} />
          )}
        </CardBody>
      </Card>
    </Flex>
  );
}

export default PrintNAsList;
